import React from 'react';
import styled from 'styled-components/macro';
import { Formik, Field } from 'formik';
import {
  Modal,
  ModalActions,
  ModalContent,
  Button,
  Input,
  Checkbox,
} from '@deeptrue-frontend/ui';
import { useFormatMessage } from 'components/IntlProvider';
import { AssetType } from 'store/modules/assets/assets.types';
import ErrorMessages from 'components/Fields/ErrorMessages';

export interface FormValues {
  name?: string;
  context_name?: string;
  is_global: boolean;
  non_field_errors?: any;
}

interface Props {
  isVisible: boolean;
  onClose: () => void;
  updateAsset: any;
  initialValues: FormValues;
  assetDetails: AssetType;
}

const StyledButton = styled(Button)`
  width: 100%;
`;

const ContentInfo = styled.div`
  display: flex;
  margin: 1rem 0;
`;

const ContentName = styled.span`
  font-weight: bold;
`;

const ContentValue = styled.span`
  overflow: hidden;
  min-width: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 0.5rem;
`;
const ContentAnchor = styled.a`
  overflow: hidden;
  min-width: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledModalAction = styled(ModalActions)`
  border: none;
`;

const AssetDetail = ({
  isVisible,
  onClose,
  updateAsset,
  initialValues,
  assetDetails,
}: Props) => {
  const t = useFormatMessage();

  return (
    <Modal
      isVisible={isVisible}
      onCloseClick={onClose}
      onBackdropClick={onClose}
    >
      <Formik onSubmit={updateAsset} initialValues={initialValues}>
        {({ handleSubmit, isSubmitting, errors, touched }) => {
          const nonFieldErrors = errors.non_field_errors;

          return (
            <form onSubmit={handleSubmit}>
              <ModalContent>
                <Field
                  name="name"
                  as={Input}
                  hasError={touched.name && errors.name}
                  helperText={touched.name && errors.name}
                  label={t('forms.assets.assetName', 'Asset Name')}
                  placeholder={t('forms.assets.assetName', 'Add Asset Name')}
                />
                <Field
                  name="context_name"
                  as={Input}
                  hasError={touched.context_name && errors.context_name}
                  helperText={touched.context_name && errors.context_name}
                  placeholder={t(
                    'forms.assets.assetContext.placeholder',
                    'Add Context Name'
                  )}
                  label={t('forms.assets.assetContext', 'Context Name')}
                />
                <Field
                  name="is_global"
                  as={Checkbox}
                  hasError={touched.is_global && errors.is_global}
                  helperText={touched.is_global && errors.is_global}
                  label={t('forms.assets.isGlobal', 'Is global?')}
                />
                <ContentInfo>
                  <ContentName>
                    {t('forms.assets.createdAt', 'Created at')}:
                  </ContentName>
                  <ContentValue>{assetDetails.created_at}</ContentValue>
                </ContentInfo>

                <ContentInfo>
                  <ContentName>
                    {t('forms.assets.language', 'Language')}:
                  </ContentName>
                  <ContentValue>{assetDetails.language}</ContentValue>
                </ContentInfo>

                <ContentInfo>
                  <ContentName>{t('forms.assets.links', 'Links')}:</ContentName>
                  <ContentAnchor
                    href={assetDetails.asset}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ContentValue>{assetDetails.asset}</ContentValue>
                  </ContentAnchor>
                </ContentInfo>

                <ContentInfo>
                  <ContentName>
                    {t('forms.assets.organizationUuid', 'Organization uuid')}:
                  </ContentName>
                  <ContentValue>
                    {assetDetails.organization_uuid || '-'}
                  </ContentValue>
                </ContentInfo>

                <ContentInfo>
                  <ContentName>
                    {t('forms.assets.updatedAt', 'Updated at')}:
                  </ContentName>
                  <ContentValue>{assetDetails.updated_at}</ContentValue>
                </ContentInfo>

                <ContentInfo>
                  <ContentName>
                    {t('forms.assets.userUuid', 'User uuid')}:
                  </ContentName>
                  <ContentValue>{assetDetails.user_uuid || '-'}</ContentValue>
                </ContentInfo>

                <ContentInfo>
                  <ContentName>{t('forms.assets.uuid', 'Uuid')}:</ContentName>
                  <ContentValue>{assetDetails.uuid || '-'}</ContentValue>
                </ContentInfo>
              </ModalContent>
              {nonFieldErrors && <ErrorMessages errors={nonFieldErrors} />}
              <StyledModalAction>
                <StyledButton
                  variant="primary"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {t('forms.assets.update', 'Update')}
                </StyledButton>
              </StyledModalAction>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AssetDetail;
